import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-construction-darkblue text-white py-12">
        <div className="container mx-auto px-4">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-construction-yellow hover:text-yellow-400 mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold">
            Terms & Conditions
          </h1>
          <p className="text-gray-300 mt-4">
            Last Updated: February 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8 md:p-12">
            
            {/* Introduction */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">1. Introduction</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  These Terms and Conditions govern your use of this website and the services provided by <strong>Construction Cards Helpline Limited</strong> (Company Number: 13678504), a company incorporated in the United Kingdom on 14th October 2021 ("we", "us", "our", or "the Company").
                </p>
                <p>
                  By accessing our website, engaging our services, or making a booking, you agree to be bound by these Terms and Conditions in full. If you do not agree with any part of these terms, you must not use our services.
                </p>
                <p>
                  Our registered office is located at 124 City Road, London EC1V 2NX, United Kingdom.
                </p>
              </div>
            </div>

            {/* Services Provided */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">2. Services Provided</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Construction Cards Helpline Limited provides advisory, booking, and administrative services relating to construction industry qualifications and certifications, including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>CSCS (Construction Skills Certification Scheme) card application assistance</li>
                  <li>CSCS test booking services</li>
                  <li>NVQ (National Vocational Qualification) Level 2 to Level 7 qualifications</li>
                  <li>CPCS (Construction Plant Competence Scheme) training and certification</li>
                  <li>NPORS (National Plant Operators Registration Scheme) training courses</li>
                  <li>Construction certification guidance and consultancy</li>
                </ul>
                <p className="font-semibold">
                  Important: The Company acts as an intermediary service provider and is NOT the issuing authority for CSCS cards, CPCS certifications, NPORS cards, NVQ awarding bodies, or any official construction industry qualifications. Final approval and issuance of all cards and qualifications remain at the sole discretion of the relevant awarding bodies and regulatory authorities.
                </p>
                <p>
                  We facilitate the application process, provide guidance, arrange bookings, and offer training services in partnership with accredited centres. We do not guarantee approval, acceptance, or issuance of any certification.
                </p>
              </div>
            </div>

            {/* Booking & Payments */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">3. Booking & Payments</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>3.1 Payment Terms:</strong> Payment is required at the time of booking or as otherwise agreed in writing. All prices are quoted in British Pounds Sterling (GBP) and are inclusive of VAT where applicable.
                </p>
                <p>
                  <strong>3.2 Confirmation:</strong> Upon receipt of payment, you will receive a booking confirmation via email. This confirmation does not guarantee test or course availability until explicitly confirmed by the relevant test centre or training provider.
                </p>
                <p>
                  <strong>3.3 Pricing:</strong> Prices are subject to change without notice. However, any confirmed bookings will honour the price agreed at the time of booking.
                </p>
                <p>
                  <strong>3.4 Third-Party Fees:</strong> Some services involve fees payable directly to third parties (e.g., test centres, awarding bodies). These fees are separate from our service charges and are non-refundable.
                </p>
                <p>
                  <strong>3.5 Payment Methods:</strong> We accept payment via bank transfer, credit/debit card, or other methods as specified during the booking process.
                </p>
              </div>
            </div>

            {/* Refund & Cancellation Policy */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">4. Refund & Cancellation Policy</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>4.1 Cancellations by Customer:</strong> Cancellation requests must be submitted in writing via email. Refund eligibility depends on the timing and nature of the service:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>More than 14 days before scheduled date:</strong> Full refund minus £50 administration fee</li>
                  <li><strong>7-14 days before scheduled date:</strong> 50% refund</li>
                  <li><strong>Less than 7 days before scheduled date:</strong> No refund</li>
                </ul>
                <p>
                  <strong>4.2 Non-Refundable Situations:</strong> No refunds will be provided in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Failure to attend a booked test or course without prior notice</li>
                  <li>Providing incorrect or incomplete information resulting in booking errors</li>
                  <li>Failure to meet eligibility requirements for the requested service</li>
                  <li>Failure to pass a test or assessment</li>
                  <li>Third-party fees already paid to test centres or awarding bodies</li>
                  <li>Cancellations made after the service has been delivered</li>
                </ul>
                <p>
                  <strong>4.3 Refund Processing:</strong> Approved refunds will be processed within 14-21 working days to the original payment method.
                </p>
                <p>
                  <strong>4.4 Cancellations by the Company:</strong> If we cancel a booking due to circumstances beyond our control, we will offer a full refund or the option to reschedule at no additional cost.
                </p>
              </div>
            </div>

            {/* Customer Responsibilities */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">5. Customer Responsibilities</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  By using our services, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate, complete, and truthful information at all times</li>
                  <li>Ensure you meet the eligibility requirements for the service you are booking</li>
                  <li>Attend all booked tests, courses, or assessments on time</li>
                  <li>Bring valid photographic identification and any required documentation</li>
                  <li>Comply with all requirements set by test centres, training providers, and awarding bodies</li>
                  <li>Inform us immediately of any changes to your personal details or circumstances</li>
                  <li>Act professionally and respectfully when attending courses or interacting with our team</li>
                </ul>
                <p>
                  <strong>Failure to fulfil these responsibilities may result in cancellation of services without refund.</strong>
                </p>
              </div>
            </div>

            {/* Course & Test Requirements */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">6. Course & Test Requirements</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>6.1 Identification:</strong> You must bring valid photographic identification (passport, driving licence, or national ID card) to all tests and courses. Failure to do so may result in being refused entry with no refund.
                </p>
                <p>
                  <strong>6.2 Punctuality:</strong> Arrive at least 15 minutes before your scheduled appointment. Late arrivals may not be permitted entry, and fees will not be refunded.
                </p>
                <p>
                  <strong>6.3 Conduct:</strong> Disruptive, abusive, or inappropriate behaviour will result in immediate removal from the premises without refund.
                </p>
                <p>
                  <strong>6.4 Health & Safety:</strong> You must comply with all health and safety regulations at training centres and test venues.
                </p>
              </div>
            </div>

            {/* NVQ & Qualification Services */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">7. NVQ & Qualification Services</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>7.1 Assessment Process:</strong> NVQ qualifications are work-based and require evidence submission, on-site assessments, and portfolio building. Completion times vary based on individual progress and evidence availability.
                </p>
                <p>
                  <strong>7.2 Evidence Requirements:</strong> You are responsible for providing sufficient evidence of competence in your trade. Failure to provide adequate evidence may delay or prevent qualification completion.
                </p>
                <p>
                  <strong>7.3 No Guaranteed Timeframes:</strong> While we provide estimated completion times, actual duration depends on multiple factors including evidence quality, assessor availability, and awarding body processing times.
                </p>
                <p>
                  <strong>7.4 No Guaranteed Pass:</strong> We do not guarantee that you will pass any assessment or receive any qualification. Success depends on your competence, evidence, and meeting awarding body standards.
                </p>
              </div>
            </div>

            {/* CPCS & NPORS Courses */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">8. CPCS & NPORS Courses</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>8.1 Course Availability:</strong> CPCS and NPORS courses are subject to availability at partner training centres. We will make every effort to secure your preferred dates.
                </p>
                <p>
                  <strong>8.2 Experience Requirements:</strong> Some courses require prior experience or qualifications. You must meet these requirements before booking.
                </p>
                <p>
                  <strong>8.3 Certification:</strong> Issuance of CPCS Red Cards, Blue Cards, or NPORS cards is subject to successful completion of tests and approval by the relevant scheme operator. We are not responsible for certification decisions.
                </p>
                <p>
                  <strong>8.4 NVQ Upgrade Requirements:</strong> CPCS Red Card holders must complete an NVQ within two years to upgrade to a Blue Card. We can assist with NVQ booking but cannot guarantee completion within any specific timeframe.
                </p>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">9. Limitation of Liability</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>9.1 Service Provision:</strong> Our liability is limited to the provision of booking, advisory, and administrative services. We are not liable for decisions made by test centres, training providers, or awarding bodies.
                </p>
                <p>
                  <strong>9.2 No Guarantee of Employment:</strong> Obtaining a CSCS card, CPCS certification, NVQ, or any other qualification does not guarantee employment. We accept no liability for employment outcomes.
                </p>
                <p>
                  <strong>9.3 Indirect Losses:</strong> We are not liable for any indirect, consequential, or economic losses including but not limited to loss of earnings, loss of business, or loss of opportunity.
                </p>
                <p>
                  <strong>9.4 Maximum Liability:</strong> Our total liability to you for any claim arising from our services shall not exceed the amount you paid for the specific service giving rise to the claim.
                </p>
                <p>
                  <strong>9.5 Force Majeure:</strong> We are not liable for failure to perform our obligations due to circumstances beyond our reasonable control including strikes, natural disasters, pandemics, or government restrictions.
                </p>
              </div>
            </div>

            {/* Data Protection & Privacy */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">10. Data Protection & Privacy</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>10.1 GDPR Compliance:</strong> We are committed to protecting your personal data in accordance with the UK General Data Protection Regulation (GDPR) and the Data Protection Act 2018.
                </p>
                <p>
                  <strong>10.2 Data Usage:</strong> Personal information collected during the booking process will be used solely for the purpose of providing our services, processing payments, and communicating with you.
                </p>
                <p>
                  <strong>10.3 Third-Party Sharing:</strong> We may share your information with test centres, training providers, and awarding bodies as necessary to deliver the services you have requested.
                </p>
                <p>
                  <strong>10.4 Privacy Policy:</strong> For full details on how we collect, use, and protect your data, please refer to our <Link to="/privacy" className="text-construction-yellow hover:underline">Privacy Policy</Link>.
                </p>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">11. Intellectual Property</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>11.1 Website Content:</strong> All content on this website, including text, graphics, logos, images, and software, is the property of Construction Cards Helpline Limited or its licensors and is protected by UK and international copyright laws.
                </p>
                <p>
                  <strong>11.2 Restrictions:</strong> You may not reproduce, distribute, modify, or create derivative works from any content on this website without our prior written consent.
                </p>
                <p>
                  <strong>11.3 Trademarks:</strong> CSCS, CPCS, NPORS, and other certification scheme names are trademarks of their respective owners. We use these terms for descriptive purposes only and do not claim ownership.
                </p>
              </div>
            </div>

            {/* Third-Party Providers */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">12. Third-Party Providers</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>12.1 Accredited Partners:</strong> We work with CITB-approved training centres, accredited test centres, and recognised awarding bodies to deliver our services.
                </p>
                <p>
                  <strong>12.2 Third-Party Changes:</strong> We are not responsible for changes to test formats, course content, pricing, or policies made by third-party providers or awarding bodies.
                </p>
                <p>
                  <strong>12.3 Third-Party Decisions:</strong> Decisions regarding test results, course outcomes, and certification approval are made solely by the relevant third parties. We cannot influence these decisions.
                </p>
              </div>
            </div>

            {/* Complaints Procedure */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">13. Complaints Procedure</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>13.1 How to Complain:</strong> If you are dissatisfied with our services, please contact us in writing via email at info@constructioncardshelpline.co.uk or by post to our registered office address.
                </p>
                <p>
                  <strong>13.2 Response Time:</strong> We will acknowledge your complaint within 3 working days and aim to provide a full response within 14 working days.
                </p>
                <p>
                  <strong>13.3 Resolution:</strong> We are committed to resolving complaints fairly and efficiently. If you remain dissatisfied after our response, you may escalate the matter through appropriate dispute resolution channels.
                </p>
              </div>
            </div>

            {/* Changes to Terms */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">14. Changes to Terms</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>14.1 Right to Update:</strong> We reserve the right to modify these Terms and Conditions at any time. Changes will be posted on this page with an updated "Last Updated" date.
                </p>
                <p>
                  <strong>14.2 Continued Use:</strong> Your continued use of our website and services after changes are posted constitutes acceptance of the updated terms.
                </p>
                <p>
                  <strong>14.3 Existing Bookings:</strong> Changes to terms will not affect bookings already confirmed and paid for prior to the update.
                </p>
              </div>
            </div>

            {/* Governing Law */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">15. Governing Law & Jurisdiction</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>15.1 Applicable Law:</strong> These Terms and Conditions shall be governed by and construed in accordance with the laws of England and Wales.
                </p>
                <p>
                  <strong>15.2 Jurisdiction:</strong> Any disputes arising from these terms or our services shall be subject to the exclusive jurisdiction of the courts of England and Wales.
                </p>
                <p>
                  <strong>15.3 Severability:</strong> If any provision of these terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-lg p-6 mt-10">
              <h3 className="text-xl font-bold text-construction-darkblue mb-4">Contact Information</h3>
              <div className="space-y-2 text-gray-700">
                <p><strong>Company Name:</strong> Construction Cards Helpline Limited</p>
                <p><strong>Company Number:</strong> 13678504</p>
                <p><strong>Registered Address:</strong> 124 City Road, London EC1V 2NX, United Kingdom</p>
                <p><strong>Email:</strong> info@constructioncardshelpline.co.uk</p>
                <p><strong>Phone:</strong> +44 7848 482492</p>
              </div>
            </div>

            {/* Footer Note */}
            <div className="mt-10 pt-6 border-t border-gray-200 text-sm text-gray-600">
              <p>
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
              <p className="mt-4">
                <strong>Last Updated:</strong> February 2026
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
